import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

const GOOGLE_MAPS_SRC =
  'https://maps.googleapis.com/maps/api/js?key=AIzaSyDjOcdDo78v7TuPUrmdX-UxAUjOx5J7KQU&loading=async';
const CASHFREE_SDK_SRC = 'https://sdk.cashfree.com/js/v3/cashfree.js';
const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
const REFER_JS_SRC = 'https://passbook-refer-and-earn.web.app/refer.js';

/**
 * Loads third-party scripts on demand so they do not block white-label
 * theme bootstrap (business/ssp/url).
 */
@Injectable({
  providedIn: 'root',
})
export class ExternalScriptLoaderService {
  private readonly inflight = new Map<string, Promise<void>>();
  private referObserverAttached = false;

  loadScript(src: string): Promise<void> {
    const existing = this.inflight.get(src);
    if (existing) {
      return existing;
    }

    if (document.querySelector(`script[src="${src}"]`)) {
      return Promise.resolve();
    }

    const promise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });

    this.inflight.set(src, promise);
    return promise;
  }

  loadGoogleMaps(): Promise<void> {
    if ((window as any).google?.maps) {
      return Promise.resolve();
    }
    return this.loadScript(GOOGLE_MAPS_SRC);
  }

  loadCashfreeSdk(): Promise<void> {
    if (typeof window.Cashfree === 'function') {
      return Promise.resolve();
    }
    return this.loadScript(CASHFREE_SDK_SRC);
  }

  loadRazorpayCheckout(): Promise<void> {
    if (typeof (window as any).Razorpay === 'function') {
      return Promise.resolve();
    }
    return this.loadScript(RAZORPAY_CHECKOUT_SRC);
  }

  /**
   * refer.js skips MutationObserver when loaded after DOM ready (defer/async).
   * Load script + attach our own observer so Angular-rendered cards still fill.
   */
  async ensureReferEarn(): Promise<void> {
    try {
      await this.loadScript(REFER_JS_SRC);
    } catch (err) {
      console.warn('refer.js failed to load', err);
      return;
    }
    this.attachReferCardObserver();
    this.renderExistingReferCards();
  }

  loadReferEarn(): Promise<void> {
    return this.ensureReferEarn();
  }

  /** Soft-preload after theme — never blocks UI. */
  preloadAfterTheme(): void {
    const run = () => {
      void this.loadCashfreeSdk().catch(() => undefined);
      void this.loadRazorpayCheckout().catch(() => undefined);
      void this.ensureReferEarn().catch(() => undefined);
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 1500);
    }
  }

  private getReferBaseUrl(): string {
    const host = window.location.hostname;
    if (host.includes('dev') || host.includes('localhost')) {
      return 'https://devpassbook-refer-earn.web.app/refer-earn?';
    }
    if (host.includes('stage')) {
      return 'https://stage-refer-earn.web.app/refer-earn?';
    }
    // Prefer env when available; fall back to prod refer host
    const fromEnv = (environment as any).referralBaseUrl;
    if (fromEnv) {
      return `${String(fromEnv).replace(/\/$/, '')}/refer-earn?`;
    }
    return 'https://passbook-refer-and-earn.web.app/refer-earn?';
  }

  private renderReferCard(container: HTMLElement): void {
    if (container.dataset['loaded'] === 'true') {
      return;
    }
    container.dataset['loaded'] = 'true';

    const cfg = {
      businessId: container.getAttribute('data-business') || '',
      mobile: container.getAttribute('data-mobile') || '',
      email: container.getAttribute('data-email') || '',
      name: container.getAttribute('data-name') || '',
      wurl: container.getAttribute('data-webUrl') || '',
      userId: container.getAttribute('data-userid') || container.getAttribute('data-userId') || '',
      token: container.getAttribute('data-token') || '',
    };

    try {
      sessionStorage.setItem('config', JSON.stringify(cfg));
    } catch {
      // ignore quota / private mode
    }

    const iframe = document.createElement('iframe');
    iframe.src =
      this.getReferBaseUrl() +
      'businessId=' +
      encodeURIComponent(cfg.businessId) +
      '&mobile=' +
      encodeURIComponent(cfg.mobile) +
      '&email=' +
      encodeURIComponent(cfg.email) +
      '&name=' +
      encodeURIComponent(cfg.name) +
      '&webUrl=' +
      encodeURIComponent(cfg.wurl) +
      '&userId=' +
      encodeURIComponent(cfg.userId) +
      '&token=' +
      encodeURIComponent(cfg.token) +
      '&loadingtype=' +
      encodeURIComponent('CARD');
    iframe.style.cssText = 'width:100%;height:100%;border:none';
    container.appendChild(iframe);
  }

  private renderExistingReferCards(): void {
    document
      .querySelectorAll<HTMLElement>('[data-simpo-refer-card]')
      .forEach((el) => this.renderReferCard(el));
  }

  private attachReferCardObserver(): void {
    if (this.referObserverAttached || !document.body) {
      return;
    }
    this.referObserverAttached = true;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }
          const el = node as HTMLElement;
          if (el.matches?.('[data-simpo-refer-card]')) {
            this.renderReferCard(el);
          }
          el.querySelectorAll?.('[data-simpo-refer-card]').forEach((card) => {
            this.renderReferCard(card as HTMLElement);
          });
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
}
